import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stepper,
    Step,
    StepLabel,
    TextField,
    MenuItem,
    Stack,
    Typography,
    IconButton,
    Autocomplete,
    CircularProgress,
    Alert,
    Divider,
    Avatar,
    Paper,
    Radio,
    RadioGroup,
    FormControlLabel,
    Chip,
    Switch,
    Tooltip,
} from "@mui/material";
import {
    Add,
    DeleteOutline,
    Person,
    Email,
    Phone,
    LocationOn,
    LocalShipping,
    Receipt,
    Inventory2,
    OpenInNew,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

import axios from "../../../utils/axios";
import useCurrency from "../../../hooks/common/useCurrency";
import { useCreateOrder } from "../../../hooks/orders/useCreateOrder";
import { useGetShippingMethods } from "../../../hooks/shipping/useGetShippingMethods";
import { useGetCustomerAddresses } from "../../../hooks/customers/useGetCustomerAddresses";
import { countries } from "../../../constants/countres";

const STEPS = ["customer", "items", "shipping", "payment"];

const formatAddress = (addr, isArabic) => {
    if (!addr) return "";
    if (addr.formatted_address) return addr.formatted_address;
    const country = countries.find((c) => c.code === addr.country_code);
    return [
        addr.street,
        addr.building && `${isArabic ? "مبنى" : "Bldg"} ${addr.building}`,
        addr.area,
        addr.city,
        country?.label || addr.country_code,
    ].filter(Boolean).join(" — ");
};

const CreateOrderModal = ({ open, onClose, onCreated }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const { format: formatPrice } = useCurrency();

    const [activeStep, setActiveStep] = useState(0);

    // Step 1 — customer (full object now)
    const [customer, setCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState("");
    const [customerOptions, setCustomerOptions] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);

    // Step 2 — items
    const [items, setItems] = useState([]);
    const [productSearch, setProductSearch] = useState("");
    const [productOptions, setProductOptions] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    // Step 3 — shipping
    const [shippingAddressId, setShippingAddressId] = useState("");
    const [billingAddressId, setBillingAddressId] = useState("");
    const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
    const [shippingMethodId, setShippingMethodId] = useState("");
    const [couponCode, setCouponCode] = useState("");

    // Step 4 — payment
    const [paymentMethod, setPaymentMethod] = useState("cod");
    const [paymentReference, setPaymentReference] = useState("");
    const [notes, setNotes] = useState("");

    const { methods: shippingMethods } = useGetShippingMethods({ per_page: 200 });
    const { createOrderMutate, isCreating } = useCreateOrder();

    // Auto-fetch the picked customer's addresses
    const { addresses, isLoading: loadingAddresses } = useGetCustomerAddresses(customer?.id);

    // Reset on open/close
    useEffect(() => {
        if (open) {
            setActiveStep(0);
            setCustomer(null);
            setCustomerSearch("");
            setCustomerOptions([]);
            setItems([]);
            setProductSearch("");
            setProductOptions([]);
            setShippingAddressId("");
            setBillingAddressId("");
            setBillingSameAsShipping(true);
            setShippingMethodId("");
            setCouponCode("");
            setPaymentMethod("cod");
            setPaymentReference("");
            setNotes("");
        }
    }, [open]);

    // Default the shipping address to the one marked as default once addresses load
    useEffect(() => {
        if (!addresses || addresses.length === 0) {
            setShippingAddressId("");
            setBillingAddressId("");
            return;
        }
        const defaultShipping =
            addresses.find((a) => a.is_default_shipping) || addresses[0];
        const defaultBilling =
            addresses.find((a) => a.is_default_billing) || defaultShipping;
        setShippingAddressId(String(defaultShipping.id));
        setBillingAddressId(String(defaultBilling.id));
    }, [addresses]);

    // ---- Customer search (debounced) ----
    // Empty search loads the most recent customers; typing filters them.
    useEffect(() => {
        if (!open || activeStep !== 0) return;
        let cancelled = false;
        const handle = setTimeout(() => {
            setLoadingCustomers(true);
            axios
                .get("/admin/customers", {
                    params: {
                        search: customerSearch || undefined,
                        per_page: 20,
                        sort: "newest",
                    },
                })
                .then((res) => {
                    if (cancelled) return;
                    const root = res.data?.data;
                    const list = Array.isArray(root)
                        ? root
                        : Array.isArray(root?.data) ? root.data : [];
                    setCustomerOptions(list);
                })
                .catch(() => {})
                .finally(() => {
                    if (!cancelled) setLoadingCustomers(false);
                });
        }, customerSearch ? 300 : 0);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [customerSearch, open, activeStep]);

    // ---- Product search (debounced) ----
    // Empty search loads the latest products; typing filters them.
    useEffect(() => {
        if (!open || activeStep !== 1) return;
        let cancelled = false;
        const handle = setTimeout(() => {
            setLoadingProducts(true);
            axios
                .get("/admin/products", {
                    params: {
                        search: productSearch || undefined,
                        per_page: 20,
                    },
                })
                .then((res) => {
                    if (!cancelled) setProductOptions(res.data?.data || []);
                })
                .catch(() => {})
                .finally(() => {
                    if (!cancelled) setLoadingProducts(false);
                });
        }, productSearch ? 300 : 0);
        return () => {
            cancelled = true;
            clearTimeout(handle);
        };
    }, [productSearch, open, activeStep]);

    const subtotalPreview = useMemo(
        () =>
            items.reduce((acc, it) => {
                const price = parseFloat(it.unit_price_override) || 0;
                const discount = parseFloat(it.discount) || 0;
                const qty = parseInt(it.qty, 10) || 0;
                return acc + (price * qty - discount);
            }, 0),
        [items]
    );

    const stepLabels = [
        isArabic ? "العميل" : "Customer",
        isArabic ? "المنتجات" : "Items",
        isArabic ? "الشحن" : "Shipping",
        isArabic ? "الدفع" : "Payment",
    ];

    const canGoNext = () => {
        if (activeStep === 0) return !!customer?.id;
        if (activeStep === 1) return items.length > 0 && items.every((it) => it.qty > 0);
        if (activeStep === 2) {
            if (!shippingAddressId || !shippingMethodId) return false;
            if (!billingSameAsShipping && !billingAddressId) return false;
            return true;
        }
        if (activeStep === 3) {
            if (paymentMethod === "manual") return !!paymentReference;
            return !!paymentMethod;
        }
        return false;
    };

    const addProduct = (product) => {
        if (!product) return;
        setItems((prev) => [
            ...prev,
            {
                product_id: product.id,
                variant_id: null,
                qty: 1,
                unit_price_override: product.price ?? "",
                discount: "",
                _meta: { product },
            },
        ]);
        setProductSearch("");
    };

    const updateItem = (idx, patch) => {
        setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    };

    const removeItem = (idx) => {
        setItems((prev) => prev.filter((_, i) => i !== idx));
    };

    const submit = () => {
        const billingId = billingSameAsShipping ? null : parseInt(billingAddressId, 10);
        const payload = {
            user_id: customer.id,
            items: items.map((it) => ({
                product_id: it.product_id,
                variant_id: it.variant_id || null,
                qty: parseInt(it.qty, 10) || 1,
                unit_price_override:
                    it.unit_price_override !== "" ? parseFloat(it.unit_price_override) : null,
                discount: it.discount !== "" ? parseFloat(it.discount) : 0,
            })),
            shipping_address_id: parseInt(shippingAddressId, 10),
            billing_address_id: billingId,
            shipping_method_id: parseInt(shippingMethodId, 10),
            coupon_code: couponCode || null,
            notes: notes || null,
            payment_method: paymentMethod,
            payment_reference: paymentMethod === "manual" ? paymentReference : null,
        };

        createOrderMutate(payload, {
            onSuccess: (res) => {
                const newId = res?.data?.id;
                onCreated?.(newId);
            },
        });
    };

    // Pretty name for the picked address (used in summary)
    const pickedShipping = addresses?.find((a) => String(a.id) === String(shippingAddressId));
    const pickedBilling = billingSameAsShipping
        ? pickedShipping
        : addresses?.find((a) => String(a.id) === String(billingAddressId));
    const pickedShippingMethod = shippingMethods?.find(
        (m) => String(m.id) === String(shippingMethodId)
    );

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? "إنشاء طلب جديد" : "Create New Order"}
            </DialogTitle>

            <DialogContent dividers>
                <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                    {stepLabels.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {/* ──────────────────────────── Step 1 — Customer */}
                {activeStep === 0 && (
                    <Stack spacing={2}>
                        <Autocomplete
                            options={customerOptions}
                            value={customer}
                            loading={loadingCustomers}
                            isOptionEqualToValue={(a, b) => a?.id === b?.id}
                            getOptionLabel={(opt) => opt?.name || ""}
                            onInputChange={(_, val) => setCustomerSearch(val)}
                            onChange={(_, val) => setCustomer(val)}
                            filterOptions={(x) => x}
                            renderOption={(props, opt) => (
                                <Box component="li" {...props} key={opt.id}>
                                    <Avatar sx={{ width: 32, height: 32, fontSize: 12, mr: 1.5, bgcolor: "primary.main" }}>
                                        {(opt.name || "?").substring(0, 2).toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                                            {opt.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ direction: "ltr" }}>
                                            {opt.email}{opt.phone ? ` • ${opt.phone}` : ""}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={isArabic ? "ابحث عن العميل" : "Search customer"}
                                    placeholder={isArabic ? "اسم، بريد، أو هاتف" : "Name, email, or phone"}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <Person fontSize="small" sx={{ ml: 0.5, mr: 1, color: "text.secondary" }} />
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {loadingCustomers && <CircularProgress size={18} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />

                        {customer && (
                            <Paper
                                variant="outlined"
                                sx={{ p: 2.5, borderRadius: 3, borderColor: "primary.main", borderWidth: 1.5 }}
                            >
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Avatar sx={{ width: 56, height: 56, fontSize: 20, bgcolor: "primary.main" }}>
                                        {(customer.name || "?").substring(0, 2).toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="h6" fontWeight={700}>
                                            {customer.name}
                                        </Typography>
                                        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: 0.5, mt: 0.5 }}>
                                            {customer.email && (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Email sx={{ fontSize: 14 }} color="action" />
                                                    <Typography variant="caption" sx={{ direction: "ltr" }}>
                                                        {customer.email}
                                                    </Typography>
                                                </Stack>
                                            )}
                                            {customer.phone && (
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Phone sx={{ fontSize: 14 }} color="action" />
                                                    <Typography variant="caption" sx={{ direction: "ltr" }}>
                                                        {customer.phone}
                                                    </Typography>
                                                </Stack>
                                            )}
                                        </Stack>
                                        <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: "wrap", rowGap: 0.5 }}>
                                            {customer.orders_count != null && (
                                                <Chip
                                                    size="small"
                                                    label={
                                                        isArabic
                                                            ? `${customer.orders_count} طلب`
                                                            : `${customer.orders_count} orders`
                                                    }
                                                    color="primary"
                                                    variant="tonal"
                                                />
                                            )}
                                            {customer.total_spent != null && (
                                                <Chip
                                                    size="small"
                                                    label={`${isArabic ? "أنفق" : "Spent"} ${formatPrice(customer.total_spent)}`}
                                                    color="success"
                                                    variant="tonal"
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                    <Tooltip title={isArabic ? "افتح صفحة العميل" : "Open customer profile"}>
                                        <IconButton
                                            component={RouterLink}
                                            to={`/customers/${customer.id}`}
                                            target="_blank"
                                            size="small"
                                        >
                                            <OpenInNew fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                )}

                {/* ──────────────────────────── Step 2 — Items */}
                {activeStep === 1 && (
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                            <Add fontSize="small" color="action" />
                            <Autocomplete
                                sx={{ flex: 1 }}
                                size="small"
                                options={productOptions}
                                loading={loadingProducts}
                                getOptionLabel={(o) =>
                                    o?.name?.[lng] || o?.name?.en || o?.name?.ar || o?.name || ""
                                }
                                onInputChange={(_, val) => setProductSearch(val)}
                                onChange={(_, val) => addProduct(val)}
                                filterOptions={(x) => x}
                                renderInput={(p) => (
                                    <TextField
                                        {...p}
                                        placeholder={isArabic ? "ابحث عن منتج..." : "Search products..."}
                                        InputProps={{
                                            ...p.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingProducts && <CircularProgress size={18} />}
                                                    {p.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Stack>

                        {items.length === 0 ? (
                            <Paper
                                variant="outlined"
                                sx={{ p: 4, textAlign: "center", borderStyle: "dashed", borderRadius: 2 }}
                            >
                                <Inventory2 sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    {isArabic
                                        ? "ابحث وأضف منتجاً للبدء."
                                        : "Search and add a product to get started."}
                                </Typography>
                            </Paper>
                        ) : (
                            <Stack divider={<Divider flexItem />} spacing={2}>
                                {items.map((it, idx) => {
                                    const productName =
                                        it._meta?.product?.name?.[lng] ||
                                        it._meta?.product?.name?.en ||
                                        it._meta?.product?.name?.ar ||
                                        "-";
                                    return (
                                        <Stack key={idx} direction="row" spacing={1.5} alignItems="center">
                                            <Avatar
                                                src={it._meta?.product?.image?.thumb || it._meta?.product?.image_url}
                                                variant="rounded"
                                                sx={{ width: 40, height: 40, bgcolor: "action.hover" }}
                                            >
                                                <Inventory2 fontSize="small" />
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {productName}
                                                </Typography>
                                            </Box>
                                            <TextField
                                                label={isArabic ? "الكمية" : "Qty"}
                                                type="number"
                                                size="small"
                                                value={it.qty}
                                                onChange={(e) =>
                                                    updateItem(idx, {
                                                        qty: Math.max(1, parseInt(e.target.value || 1, 10)),
                                                    })
                                                }
                                                inputProps={{ min: 1 }}
                                                sx={{ width: 80 }}
                                            />
                                            <TextField
                                                label={isArabic ? "السعر" : "Price"}
                                                type="number"
                                                size="small"
                                                value={it.unit_price_override}
                                                onChange={(e) => updateItem(idx, { unit_price_override: e.target.value })}
                                                inputProps={{ min: 0, step: "0.01" }}
                                                sx={{ width: 110 }}
                                            />
                                            <TextField
                                                label={isArabic ? "خصم" : "Discount"}
                                                type="number"
                                                size="small"
                                                value={it.discount}
                                                onChange={(e) => updateItem(idx, { discount: e.target.value })}
                                                inputProps={{ min: 0, step: "0.01" }}
                                                sx={{ width: 100 }}
                                            />
                                            <IconButton size="small" color="error" onClick={() => removeItem(idx)}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                )}

                {/* ──────────────────────────── Step 3 — Shipping */}
                {activeStep === 2 && (
                    <Stack spacing={2.5}>
                        {/* Shipping address */}
                        <Box>
                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                <LocalShipping fontSize="small" color="primary" />
                                <Typography variant="subtitle2" fontWeight={700}>
                                    {isArabic ? "عنوان الشحن" : "Shipping Address"}
                                </Typography>
                            </Stack>

                            {loadingAddresses ? (
                                <Typography variant="body2" color="text.secondary">
                                    {isArabic ? "جاري تحميل العناوين..." : "Loading addresses..."}
                                </Typography>
                            ) : (addresses || []).length === 0 ? (
                                <Alert severity="warning">
                                    {isArabic
                                        ? "لا توجد عناوين محفوظة لهذا العميل. أضف عنواناً من صفحة العميل أولاً."
                                        : "This customer has no saved addresses. Add one from the customer page first."}
                                    {customer?.id && (
                                        <Button
                                            component={RouterLink}
                                            to={`/customers/${customer.id}?tab=addresses`}
                                            size="small"
                                            sx={{ ml: 1 }}
                                        >
                                            {isArabic ? "افتح العميل" : "Open Customer"}
                                        </Button>
                                    )}
                                </Alert>
                            ) : (
                                <RadioGroup
                                    value={shippingAddressId}
                                    onChange={(e) => {
                                        setShippingAddressId(e.target.value);
                                        if (billingSameAsShipping) setBillingAddressId(e.target.value);
                                    }}
                                >
                                    <Stack spacing={1}>
                                        {addresses.map((addr) => (
                                            <Paper
                                                key={addr.id}
                                                variant="outlined"
                                                sx={{
                                                    p: 1.5,
                                                    borderRadius: 2,
                                                    borderWidth: String(addr.id) === String(shippingAddressId) ? 2 : 1,
                                                    borderColor: (t) =>
                                                        String(addr.id) === String(shippingAddressId)
                                                            ? t.palette.primary.main
                                                            : t.palette.divider,
                                                }}
                                            >
                                                <FormControlLabel
                                                    value={String(addr.id)}
                                                    control={<Radio />}
                                                    sx={{ alignItems: "flex-start", m: 0, width: "100%" }}
                                                    label={
                                                        <Box sx={{ width: "100%" }}>
                                                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5} flexWrap="wrap">
                                                                <Typography variant="body2" fontWeight="bold">
                                                                    {addr.label || (isArabic ? "عنوان" : "Address")}
                                                                </Typography>
                                                                {addr.is_default_shipping && (
                                                                    <Chip
                                                                        size="small"
                                                                        icon={<LocalShipping sx={{ fontSize: 12 }} />}
                                                                        label={isArabic ? "شحن افتراضي" : "Default Shipping"}
                                                                        color="primary"
                                                                        variant="tonal"
                                                                        sx={{ height: 20, fontSize: 10 }}
                                                                    />
                                                                )}
                                                                {addr.is_default_billing && (
                                                                    <Chip
                                                                        size="small"
                                                                        icon={<Receipt sx={{ fontSize: 12 }} />}
                                                                        label={isArabic ? "فاتورة افتراضية" : "Default Billing"}
                                                                        color="success"
                                                                        variant="tonal"
                                                                        sx={{ height: 20, fontSize: 10 }}
                                                                    />
                                                                )}
                                                            </Stack>
                                                            <Stack direction="row" spacing={1} alignItems="center">
                                                                <LocationOn sx={{ fontSize: 14 }} color="action" />
                                                                <Typography variant="caption" color="text.secondary">
                                                                    {formatAddress(addr, isArabic)}
                                                                </Typography>
                                                            </Stack>
                                                            {addr.phone && (
                                                                <Typography variant="caption" color="text.secondary" sx={{ direction: "ltr", display: "block", mt: 0.25 }}>
                                                                    {isArabic ? "هاتف:" : "Phone:"} {addr.phone}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    }
                                                />
                                            </Paper>
                                        ))}
                                    </Stack>
                                </RadioGroup>
                            )}
                        </Box>

                        {/* Billing address toggle + selector */}
                        {(addresses || []).length > 0 && (
                            <Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={billingSameAsShipping}
                                            onChange={(e) => setBillingSameAsShipping(e.target.checked)}
                                        />
                                    }
                                    label={isArabic ? "عنوان الفاتورة نفس عنوان الشحن" : "Billing same as shipping"}
                                />

                                {!billingSameAsShipping && (
                                    <TextField
                                        select
                                        label={isArabic ? "عنوان الفاتورة" : "Billing Address"}
                                        value={billingAddressId}
                                        onChange={(e) => setBillingAddressId(e.target.value)}
                                        fullWidth
                                        sx={{ mt: 1 }}
                                    >
                                        {addresses.map((a) => (
                                            <MenuItem key={a.id} value={String(a.id)}>
                                                {a.label || `#${a.id}`} — {formatAddress(a, isArabic)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            </Box>
                        )}

                        <Divider />

                        {/* Shipping method */}
                        <TextField
                            select
                            label={isArabic ? "طريقة الشحن" : "Shipping Method"}
                            value={shippingMethodId}
                            onChange={(e) => setShippingMethodId(e.target.value)}
                            fullWidth
                            required
                        >
                            {(shippingMethods || []).length === 0 ? (
                                <MenuItem disabled value="">
                                    {isArabic ? "لا توجد طرق شحن" : "No shipping methods"}
                                </MenuItem>
                            ) : (
                                shippingMethods.map((m) => (
                                    <MenuItem key={m.id} value={m.id}>
                                        {m.name?.[lng] || m.name?.en || m.name?.ar || m.name}
                                        {m.price != null ? ` — ${formatPrice(m.price)}` : ""}
                                    </MenuItem>
                                ))
                            )}
                        </TextField>

                        {/* Coupon */}
                        <TextField
                            label={isArabic ? "كود الكوبون (اختياري)" : "Coupon Code (optional)"}
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            fullWidth
                        />
                    </Stack>
                )}

                {/* ──────────────────────────── Step 4 — Payment + review */}
                {activeStep === 3 && (
                    <Stack spacing={2}>
                        <TextField
                            select
                            label={isArabic ? "طريقة الدفع" : "Payment Method"}
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value)}
                            fullWidth
                            required
                        >
                            <MenuItem value="cod">{isArabic ? "الدفع عند الاستلام" : "Cash on Delivery"}</MenuItem>
                            <MenuItem value="paymob">Paymob</MenuItem>
                            <MenuItem value="manual">{isArabic ? "يدوي" : "Manual"}</MenuItem>
                        </TextField>

                        {paymentMethod === "manual" && (
                            <TextField
                                label={isArabic ? "مرجع الدفع" : "Payment Reference"}
                                value={paymentReference}
                                onChange={(e) => setPaymentReference(e.target.value)}
                                placeholder={isArabic ? "مثال: INV-2026-0001" : "e.g: INV-2026-0001"}
                                fullWidth
                                required
                            />
                        )}

                        <TextField
                            label={isArabic ? "ملاحظات (اختياري)" : "Notes (optional)"}
                            multiline
                            rows={3}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            fullWidth
                        />

                        <Divider />

                        {/* Review summary */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                                {isArabic ? "ملخص الطلب" : "Order Summary"}
                            </Typography>
                            <Stack spacing={1}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {isArabic ? "العميل" : "Customer"}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {customer?.name || "-"}
                                    </Typography>
                                </Stack>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        {isArabic ? "عدد المنتجات" : "Items"}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                        {items.length}
                                    </Typography>
                                </Stack>
                                {pickedShipping && (
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                        <Typography variant="body2" color="text.secondary">
                                            {isArabic ? "عنوان الشحن" : "Shipping"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ textAlign: isArabic ? "left" : "right", maxWidth: 320 }}>
                                            {formatAddress(pickedShipping, isArabic)}
                                        </Typography>
                                    </Stack>
                                )}
                                {pickedBilling && pickedBilling.id !== pickedShipping?.id && (
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                        <Typography variant="body2" color="text.secondary">
                                            {isArabic ? "عنوان الفاتورة" : "Billing"}
                                        </Typography>
                                        <Typography variant="caption" sx={{ textAlign: isArabic ? "left" : "right", maxWidth: 320 }}>
                                            {formatAddress(pickedBilling, isArabic)}
                                        </Typography>
                                    </Stack>
                                )}
                                {pickedShippingMethod && (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {isArabic ? "طريقة الشحن" : "Shipping Method"}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {pickedShippingMethod.name?.[lng] ||
                                                pickedShippingMethod.name?.en ||
                                                pickedShippingMethod.name?.ar ||
                                                pickedShippingMethod.name}
                                        </Typography>
                                    </Stack>
                                )}
                                {couponCode && (
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="body2" color="text.secondary">
                                            {isArabic ? "كوبون" : "Coupon"}
                                        </Typography>
                                        <Chip size="small" label={couponCode} color="primary" variant="tonal" />
                                    </Stack>
                                )}
                                <Divider />
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" fontWeight="bold">
                                        {isArabic ? "الإجمالي التقريبي" : "Approx. Total"}
                                    </Typography>
                                    <Box sx={{ textAlign: isArabic ? "left" : "right" }}>
                                        <Typography variant="h6" fontWeight={700} color="primary.main">
                                            {formatPrice(subtotalPreview)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {isArabic ? "بدون شحن/ضريبة" : "before shipping/tax"}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Stack>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
                <Button onClick={onClose} disabled={isCreating}>
                    {isArabic ? "إلغاء" : "Cancel"}
                </Button>

                <Stack direction="row" spacing={1}>
                    <Button
                        onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                        disabled={activeStep === 0 || isCreating}
                    >
                        {isArabic ? "السابق" : "Back"}
                    </Button>
                    {activeStep < STEPS.length - 1 ? (
                        <Button
                            variant="contained"
                            onClick={() => setActiveStep((s) => s + 1)}
                            disabled={!canGoNext()}
                        >
                            {isArabic ? "التالي" : "Next"}
                        </Button>
                    ) : (
                        <Button
                            variant="contained"
                            color="success"
                            onClick={submit}
                            disabled={!canGoNext() || isCreating}
                        >
                            {isCreating
                                ? (isArabic ? "جاري الإنشاء..." : "Creating...")
                                : (isArabic ? "إنشاء الطلب" : "Create Order")}
                        </Button>
                    )}
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default CreateOrderModal;
